import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { ConfigureLoginForm, ConfigureLoginFormSchema } from "../schemas/configure_login_form";
import { Enterprise } from "../../domain/models/enterprise";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * useConfigureLoginViewModel
 */

export const useConfigureLoginViewModel = () => {
  const {
    auth: {
      getEnterprisesUseCase,
      configureLoginUseCase,
      loginAvailableUseCase,
    },
  } = useDependencyInjection();

  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState<Enterprise | undefined>();
  const [userExisting, setUserExisting] = useState(false);
  const [newLogin, setNewLogin] = useState<string | undefined>();
  const [newLoginError, setNewLoginError] = useState(false);
  const [newLoginErrorMessage, setNewLoginErrorMessage] = useState("");
  const [newLoginAccepted, setNewLoginAccepted] = useState<string | undefined>();
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const hookForm = useForm<ConfigureLoginForm>({
    resolver: zodResolver(ConfigureLoginFormSchema),
  });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const e = await getEnterprisesUseCase.getEnterprises();
        setEnterprises(e);
      } catch (e: unknown) {
        if (typeof e === "string") {
          hookForm.setError("root", { message: e });
        }
        else if (e instanceof Error) {
          hookForm.setError("root", { message: e.message });
        }
        else {
          hookForm.setError("root", { message: "Falha ao buscar empresas!" });
        }
      }
      finally {
        setLoading(false)
      }
    })();
  }, [hookForm, getEnterprisesUseCase]);

  const filteredEnterprises = useMemo(() => {
    return search.trim().length >= 2
      ? enterprises.filter((x) => x.nome.toLowerCase().includes(search.toLowerCase()))
      : [];
  }, [search, enterprises]);

  const handleBackClick = useCallback(() => {
    switch (step) {
      case 1:
        navigate("/login", { replace: true });
        break;

      case 3:
        setNewLogin(undefined);
        setStep(step - 1);
        break;

      default:
        setStep(step - 1);
        break;
    }
  }, [step, navigate]);

  const handleSelectedEnterprise = useCallback((enterprise: Enterprise) => {
    setSelectedEnterprise(enterprise);
    setStep((x) => x + 1);
  }, []);

  const handleSubmit = hookForm.handleSubmit(useCallback(async (form: ConfigureLoginForm) => {
    try {
      setLoading(true);

      if (selectedEnterprise === undefined) {
        throw new Error("Falha ao buscar informações de login, empresa selecionada invalida!");
      }

      const configureLogin = await configureLoginUseCase.configureLogin(
        form.loginAntigo,
        form.oldPassword,
        form.loginNovo,
        form.newPassword,
        form.newPasswordConf,
        selectedEnterprise!.id!,
        newLogin && newLogin?.trim().length > 0 ? newLogin : undefined,
      );

      setUserExisting(configureLogin.existing);

      if (configureLogin.goToLastStep === true) {
        setStep(4);
      }

      if (configureLogin.goToThirdStep === true) {
        setStep(3);
      }

      if (configureLogin.message) {
        hookForm.setError("root", { message: configureLogin.message });
      }
    } catch (e: unknown) {
      if (typeof e === "string") {
        hookForm.setError("root", { message: e });
      }
      else if (e instanceof Error) {
        hookForm.setError("root", { message: e.message });
      }
      else {
        hookForm.setError("root", { message: "Falha ao buscar empresas!" });
      }
    }
    finally {
      setLoading(false)
    }
  }, [hookForm, configureLoginUseCase, selectedEnterprise, newLogin]));

  const handleNewLogin = useCallback(async () => {
    try {
      if (selectedEnterprise === undefined) {
        throw new Error("Falha ao buscar informações de login, empresa selecionada invalida!");
      }
      if (newLogin) {
        if (newLogin.trim().length < 2) {
          setNewLoginError(false);
          setNewLoginErrorMessage("");
          setNewLoginAccepted(undefined);
          return;
        }
        const response = await loginAvailableUseCase.loginAvailable(newLogin, selectedEnterprise.id);
        if (!response) {
          setNewLoginError(true);
          setNewLoginErrorMessage("Este login já está em uso por outro usuário");
          setNewLoginAccepted(undefined);
        } else {
          setNewLoginError(false);
          setNewLoginErrorMessage("");
          setNewLoginAccepted("Este login pode ser usado!");
        }
      }
    } catch (e: unknown) {
      if (typeof e === "string") {
        hookForm.setError("root", { message: e });
      }
      else if (e instanceof Error) {
        hookForm.setError("root", { message: e.message });
      }
      else {
        hookForm.setError("root", { message: "Falha ao buscar empresas!" });
      }
    }
  }, [hookForm, newLogin, selectedEnterprise, loginAvailableUseCase]);

  const handleFinish = useCallback(() => {
    navigate("/login")
  }, [navigate]);

  return ({
    hookForm,
    search,
    step,
    filteredEnterprises,
    userExisting,
    newLogin,
    newLoginError,
    newLoginErrorMessage,
    newLoginAccepted,
    loading,
    handleSubmit,
    handleBackClick,
    handleSelectedEnterprise,
    handleNewLogin,
    handleFinish,
    setSearch,
    setNewLogin,
  });
};